import { SupportedLanguage } from './site.config';

export interface TranslationStrings {
  // Meta/SEO
  siteTitle: string;
  siteDescription: string;
  hiddenH1: string;
  
  // Header
  headerTitle: string;
  headerSubtitle: string;
  
  // Buttons
  reset: string;
  download: string;
  
  // Tabs
  templates: string;
  settings: string;
  adjustments: string;
  text: string;
  background: string;
  filters: string;
  
  // Messages
  templateCreated: string;
  gridReady: string;
  resetDone: string;
  allChangesReverted: string;
  preparing: string;
  creatingCollage: string;
  done: string;
  collageSaved: string;
  error: string;
  couldNotSave: string;
  newText: string;
  clickOrDragHint: string;
  imageHintDragZoom: string;
  imageHintDragZoomMobile: string;
  
  // Features section
  featuresTitle: string;
  featuresSubtitle: string;
  feature1Title: string;
  feature1Description: string;
  feature2Title: string;
  feature2Description: string;
  feature3Title: string;
  feature3Description: string;
  feature4Title: string;
  feature4Description: string;
  
  // Footer
  footerCopyright: string;
  privacyPolicy: string;
  contact: string;
  
  // Privacy page
  privacyTitle: string;
  privacyContent: string;
  
  // Contact page
  contactTitle: string;
  contactDescription: string;
  contactName: string;
  contactEmail: string;
  contactMessage: string;
  contactSend: string;
  contactSuccess: string;
  showEmail: string;
  clickToSendEmail: string;
  noEmailConfigured: string;
  
  // Admin
  adminTitle: string;
  adminLogin: string;
  adminPassword: string;
  adminEnter: string;
  adminWrongPassword: string;
  adminSaveInfo: string;
  
  // Collage Editor UI
  upload: string;
  allTemplates: string;
  hideTemplates: string;
  createTemplate: string;
  templateGenerator: string;
  rows: string;
  columns: string;
  cellsWillBeCreated: string;
  applyTemplate: string;
  canvasSize: string;
  selectSize: string;
  width: string;
  height: string;
  cellGap: string;
  cellRounding: string;
  canvasBorder: string;
  cellBorder: string;
  borderColor: string;
  collageBackground: string;
  uploadBackground: string;
  colors: string;
  gradients: string;
  captions: string;
  add: string;
  clickAddToCreateCaption: string;
  enterText: string;
  font: string;
  size: string;
  color: string;
  shadow: string;
  stroke: string;
  strokeColor: string;
  strokeWidth: string;
  dragTextOnCanvas: string;
  textAlignment: string;
  brightness: string;
  contrast: string;
  saturation: string;
  faqTitle: string;
  
  // Adjustments Panel
  imageEnhancement: string;
  resetAdjustments: string;
  adjustmentsApplyToAll: string;
  
  // Filters Panel
  filtersTitle: string;
  filterOriginal: string;
  filter1977: string;
  filterBrannan: string;
  filterGotham: string;
  filterHefe: string;
  filterGraymouse: string;
  filterKelvin: string;
  filterNashville: string;
  filterXpro2: string;
  filterAzure: string;
  filterMist: string;
  filterPurple: string;
  filterNature: string;
  filterVintage: string;
  filterWarm: string;
  filterCool: string;
  filterSunrise: string;
  filterNoir: string;
  
  // Image Editor
  replaceImage: string;
  undo: string;
  redo: string;
  flipHorizontal: string;
  flipVertical: string;
  deleteImage: string;
  confirm: string;
  moveUp: string;
  moveDown: string;
  moveLeft: string;
  moveRight: string;
  scaleDown: string;
  scaleUp: string;
  
  // Text Size
  textSize: string;
  textSizeSmall: string;
  textSizeMedium: string;
  textSizeLarge: string;
  
  // Social Share
  shareWithFriends: string;
  shareOn: string;
  copyLink: string;
  linkCopied: string;
  
  // Size Presets
  sizeSquare: string;
  sizeInstagram: string;
  sizeFullHD: string;
  sizeStory: string;
  sizeFacebook: string;
  sizeTwitter: string;
  size2K: string;
  
  // Stickers
  stickers: string;
  uploadSticker: string;
  noStickersYet: string;
  stickerLabel: string;
  stickerScale: string;
  stickerRotation: string;
  dragStickerOnCanvas: string;
}

export const translations: Record<SupportedLanguage, TranslationStrings> = {
  en: {
    // Meta/SEO
    siteTitle: 'CollageEditor — Create Beautiful Collages Online',
    siteDescription: 'Free online photo collage editor. Choose templates, upload photos, customize style and download your collage.',
    hiddenH1: 'Online Collage Maker',
    
    // Header
    headerTitle: 'CollageEditor',
    headerSubtitle: 'Create beauty',
    
    // Buttons
    reset: 'Reset',
    download: 'Download',
    
    // Tabs
    templates: 'Templates',
    settings: 'Settings',
    adjustments: 'Adjustments',
    text: 'Text',
    background: 'Background',
    filters: 'Filters',
    
    // Messages
    templateCreated: 'Template created',
    gridReady: 'Grid is ready',
    resetDone: 'Reset',
    allChangesReverted: 'All changes reverted',
    preparing: 'Preparing...',
    creatingCollage: 'Creating your collage',
    done: 'Done!',
    collageSaved: 'Collage saved as',
    error: 'Error',
    couldNotSave: 'Could not save collage',
    newText: 'New text',
    clickOrDragHint: 'Click on a cell or drag an image',
    imageHintDragZoom: 'Drag • Scroll to zoom',
    imageHintDragZoomMobile: 'Drag • Pinch to zoom',
    
    // Features section
    featuresTitle: 'Why CollageEditor?',
    featuresSubtitle: 'Everything you need to create stunning photo collages',
    feature1Title: 'Easy to Use',
    feature1Description: 'Intuitive drag-and-drop interface. No design skills required — create professional collages in minutes.',
    feature2Title: 'Beautiful Templates',
    feature2Description: 'Choose from a variety of pre-designed templates or create your own custom grid layouts.',
    feature3Title: 'Advanced Editing',
    feature3Description: 'Apply filters, adjust colors, add text overlays, and fine-tune every detail of your collage.',
    feature4Title: 'High Quality Export',
    feature4Description: 'Download your collages in high resolution PNG or JPG format, perfect for printing or sharing.',
    
    // Footer
    footerCopyright: '© 2026 CollageEditor. All rights reserved.',
    privacyPolicy: 'Privacy Policy',
    contact: 'Contact',
    
    // Privacy page
    privacyTitle: 'Privacy Policy',
    privacyContent: `This Privacy Policy describes how CollageEditor handles user information.

### 1. What information we do NOT collect

We do not store, transmit, or upload:

- photos and images

- personal user files

All image processing takes place directly in your browser.

### 2. What information we may collect

We may collect limited technical information:

- browser type

- device type

- operating system

- user country

- anonymous usage statistics

This data is used only to improve the service.

### 3. Cookies and analytics

We may use cookies and third-party analytics services (e.g., Google Analytics).

Cookies do not contain personal data and can be disabled in your browser settings.

### 4. Third-party services

We may use third-party services for:

- analytics

- advertising

- payment processing (if applicable)

Such services have their own privacy policies.

### 5. Data protection

We take reasonable measures to protect the website.

Images are not stored on our servers.

### 6. Children

The service is not intended for persons under 13 years of age.

### 7. Policy changes

The policy may be updated. The current version is always available on this page.

### 8. Contact

For all inquiries, please contact us at https://collageeditor.com/contact`,
    
    // Contact page
    contactTitle: 'Contact Us',
    contactDescription: 'Have questions or feedback? We would love to hear from you!',
    contactName: 'Your Name',
    contactEmail: 'Your Email',
    contactMessage: 'Message',
    contactSend: 'Send Message',
    contactSuccess: 'Message sent successfully!',
    showEmail: 'Show Email',
    clickToSendEmail: 'Click to send an email',
    noEmailConfigured: 'Contact email not configured',
    
    // Admin
    adminTitle: 'Admin Panel',
    adminLogin: 'Login',
    adminPassword: 'Password',
    adminEnter: 'Enter',
    adminWrongPassword: 'Wrong password',
    adminSaveInfo: 'Changes are shown for preview. To apply them permanently, edit the translation files in the codebase.',
    
    // Collage Editor UI
    upload: 'Upload',
    allTemplates: 'All templates',
    hideTemplates: 'Hide templates',
    createTemplate: 'Create template',
    templateGenerator: 'Template Generator',
    rows: 'Rows',
    columns: 'Columns',
    cellsWillBeCreated: 'cells will be created',
    applyTemplate: 'Apply template',
    canvasSize: 'Canvas size',
    selectSize: 'Select size',
    width: 'Width',
    height: 'Height',
    cellGap: 'Cell gap',
    cellRounding: 'Cell rounding',
    canvasBorder: 'Canvas border',
    cellBorder: 'Cell border',
    borderColor: 'Border color',
    collageBackground: 'Collage background',
    uploadBackground: 'Upload background',
    colors: 'Colors',
    gradients: 'Gradients',
    captions: 'Captions',
    add: 'Add',
    clickAddToCreateCaption: 'Click "Add" to create a caption',
    enterText: 'Enter text...',
    font: 'Font',
    size: 'Size',
    color: 'Color',
    shadow: 'Shadow',
    stroke: 'Stroke',
    strokeColor: 'Stroke color',
    strokeWidth: 'Stroke width',
    dragTextOnCanvas: 'Drag text on canvas',
    textAlignment: 'Alignment',
    brightness: 'Brightness',
    contrast: 'Contrast',
    saturation: 'Saturation',
    faqTitle: 'FAQ',
    
    // Adjustments Panel
    imageEnhancement: 'Image Enhancement',
    resetAdjustments: 'Reset',
    adjustmentsApplyToAll: 'Changes apply to entire collage',
    
    // Filters Panel
    filtersTitle: 'Filters',
    filterOriginal: 'Original',
    filter1977: '1977',
    filterBrannan: 'Brannan',
    filterGotham: 'Gotham',
    filterHefe: 'Hefe',
    filterGraymouse: 'Gray Mouse',
    filterKelvin: 'Kelvin',
    filterNashville: 'Nashville',
    filterXpro2: 'X-PRO II',
    filterAzure: 'Azure',
    filterMist: 'Mist',
    filterPurple: 'Purple',
    filterNature: 'Nature',
    filterVintage: 'Vintage',
    filterWarm: 'Warm',
    filterCool: 'Cool',
    filterSunrise: 'Sunrise',
    filterNoir: 'Noir',
    
    // Image Editor
    replaceImage: 'Replace image',
    undo: 'Undo',
    redo: 'Redo',
    flipHorizontal: 'Flip horizontal',
    flipVertical: 'Flip vertical',
    deleteImage: 'Delete',
    confirm: 'Confirm',
    moveUp: 'Up',
    moveDown: 'Down',
    moveLeft: 'Left',
    moveRight: 'Right',
    scaleDown: 'Scale down',
    scaleUp: 'Scale up',
    
    // Text Size
    textSize: 'Text size',
    textSizeSmall: 'S',
    textSizeMedium: 'M',
    textSizeLarge: 'L',
    
    // Social Share
    shareWithFriends: 'Share with friends',
    shareOn: 'Share on',
    copyLink: 'Copy link',
    linkCopied: 'Link copied!',
    
    // Size Presets
    sizeSquare: 'Square',
    sizeInstagram: 'Instagram',
    sizeFullHD: 'Full HD',
    sizeStory: 'Story',
    sizeFacebook: 'Facebook',
    sizeTwitter: 'Twitter',
    size2K: '2K',
    
    // Stickers
    stickers: 'Stickers',
    uploadSticker: 'Upload Sticker',
    noStickersYet: 'No stickers yet. Upload images to use as stickers.',
    stickerLabel: 'Sticker',
    stickerScale: 'Size',
    stickerRotation: 'Rotation',
    dragStickerOnCanvas: 'Drag stickers on the canvas to reposition',
  },
  
  ua: {
    // Meta/SEO
    siteTitle: 'CollageEditor — Створюй красиві колажі онлайн',
    siteDescription: 'Безкоштовний онлайн редактор фотоколажів. Обирай шаблони, завантажуй фото, налаштовуй стиль та завантажуй готовий колаж.',
    hiddenH1: 'Онлайн редактор фотоколажів',
    
    // Header
    headerTitle: 'CollageEditor',
    headerSubtitle: 'Створюй красу',
    
    // Buttons
    reset: 'Скинути',
    download: 'Завантажити',
    
    // Tabs
    templates: 'Шаблони',
    settings: 'Налаштування',
    adjustments: 'Корекція',
    text: 'Текст',
    background: 'Фон',
    filters: 'Фільтри',
    
    // Messages
    templateCreated: 'Шаблон створено',
    gridReady: 'Сітка готова',
    resetDone: 'Скинуто',
    allChangesReverted: 'Усі зміни скасовано',
    preparing: 'Підготовка...',
    creatingCollage: 'Створюємо ваш колаж',
    done: 'Готово!',
    collageSaved: 'Колаж збережено у форматі',
    error: 'Помилка',
    couldNotSave: 'Не вдалося зберегти колаж',
    newText: 'Новий текст',
    clickOrDragHint: 'Натисніть на клітинку або перетягніть зображення',
    imageHintDragZoom: 'Перетягніть • Прокрутка для зуму',
    imageHintDragZoomMobile: 'Перетягніть • Щипок для зуму',
    
    // Features section
    featuresTitle: 'Чому CollageEditor?',
    featuresSubtitle: 'Усе необхідне для створення приголомшливих фотоколажів',
    feature1Title: 'Простий у використанні',
    feature1Description: 'Інтуїтивний інтерфейс перетягування. Не потрібні навички дизайну — створюйте професійні колажі за лічені хвилини.',
    feature2Title: 'Красиві шаблони',
    feature2Description: 'Обирайте з різноманітних готових шаблонів або створюйте власні макети сітки.',
    feature3Title: 'Розширене редагування',
    feature3Description: 'Застосовуйте фільтри, налаштовуйте кольори, додавайте текст та вдосконалюйте кожну деталь колажу.',
    feature4Title: 'Високоякісний експорт',
    feature4Description: 'Завантажуйте колажі у високій роздільній здатності PNG або JPG, ідеально для друку чи поширення.',
    
    // Footer
    footerCopyright: '© 2026 CollageEditor. Усі права захищено.',
    privacyPolicy: 'Політика конфіденційності',
    contact: 'Контакти',
    
    // Privacy page
    privacyTitle: 'Політика конфіденційності',
    privacyContent: `Ця Політика конфіденційності описує, як сервіс CollageEditor обробляє інформацію користувачів.

### 1. Яку інформацію ми не збираємо

Ми не зберігаємо, не передаємо та не завантажуємо:

- фотографії та зображення

- особисті файли користувачів

Уся обробка зображень відбувається безпосередньо у вашому браузері.

### 2. Яку інформацію ми можемо збирати

Ми можемо збирати обмежену технічну інформацію:

- тип браузера

- тип пристрою

- операційну систему

- країну користувача

- анонімну статистику використання

Ці дані використовуються лише для покращення сервісу.

### 3. Cookies та аналітика

Ми можемо використовувати cookies та сторонні аналітичні сервіси (наприклад, Google Analytics).

Cookies не містять персональних даних і можуть бути вимкнені у налаштуваннях браузера.

### 4. Сторонні сервіси

Ми можемо використовувати сторонні сервіси для:

- аналітики

- реклами

- обробки платежів (за наявності)

Такі сервіси мають власні політики конфіденційності.

### 5. Захист даних

Ми вживаємо розумних заходів для захисту сайту.

Зображення не зберігаються на наших серверах.

### 6. Діти

Сервіс не призначений для осіб молодше 13 років.

### 7. Зміни до політики

Політика може оновлюватися. Актуальна версія завжди доступна на цій сторінці.

### 8. Контакти

З усіх питань звертайтеся за адресою https://collageeditor.com/ua/contact`,
    
    // Contact page
    contactTitle: 'Зв\'яжіться з нами',
    contactDescription: 'Маєте запитання чи відгуки? Ми раді почути від вас!',
    contactName: 'Ваше ім\'я',
    contactEmail: 'Ваш Email',
    contactMessage: 'Повідомлення',
    contactSend: 'Надіслати',
    contactSuccess: 'Повідомлення успішно надіслано!',
    showEmail: 'Показати Email',
    clickToSendEmail: 'Натисніть щоб надіслати листа',
    noEmailConfigured: 'Email для зв\'язку не налаштовано',
    
    // Admin
    adminTitle: 'Адмін панель',
    adminLogin: 'Вхід',
    adminPassword: 'Пароль',
    adminEnter: 'Увійти',
    adminWrongPassword: 'Невірний пароль',
    adminSaveInfo: 'Зміни показуються для попереднього перегляду. Щоб застосувати їх назавжди, відредагуйте файли перекладів у кодовій базі.',
    
    // Collage Editor UI
    upload: 'Завантажити',
    allTemplates: 'Всі шаблони',
    hideTemplates: 'Сховати шаблони',
    createTemplate: 'Створити шаблон',
    templateGenerator: 'Генератор шаблонів',
    rows: 'Рядів',
    columns: 'Колонок',
    cellsWillBeCreated: 'буде створено комірок',
    applyTemplate: 'Застосувати шаблон',
    canvasSize: 'Розмір полотна',
    selectSize: 'Оберіть розмір',
    width: 'Ширина',
    height: 'Висота',
    cellGap: 'Відступи між комірками',
    cellRounding: 'Заокруглення комірок',
    canvasBorder: 'Рамка полотна',
    cellBorder: 'Рамка комірок',
    borderColor: 'Колір рамки',
    collageBackground: 'Фон колажу',
    uploadBackground: 'Завантажити свій фон',
    colors: 'Кольори',
    gradients: 'Градієнти',
    captions: 'Написи',
    add: 'Додати',
    clickAddToCreateCaption: 'Натисніть "Додати" щоб створити напис',
    enterText: 'Введіть текст...',
    font: 'Шрифт',
    size: 'Розмір',
    color: 'Колір',
    shadow: 'Тінь',
    stroke: 'Контур',
    strokeColor: 'Колір контуру',
    strokeWidth: 'Товщина контуру',
    dragTextOnCanvas: 'Перетягніть текст на полотно',
    textAlignment: 'Вирівнювання',
    brightness: 'Яскравість',
    contrast: 'Контраст',
    saturation: 'Насиченість',
    faqTitle: 'FAQ',
    
    // Adjustments Panel
    imageEnhancement: 'Покращення зображення',
    resetAdjustments: 'Скинути',
    adjustmentsApplyToAll: 'Зміни застосовуються до всього колажу',
    
    // Filters Panel
    filtersTitle: 'Фільтри',
    filterOriginal: 'Оригінал',
    filter1977: '1977',
    filterBrannan: 'Бреннан',
    filterGotham: 'Готем',
    filterHefe: 'Хефе',
    filterGraymouse: 'Сіра миша',
    filterKelvin: 'Кельвін',
    filterNashville: 'Нешвілл',
    filterXpro2: 'X-PRO II',
    filterAzure: 'Лазур',
    filterMist: 'Імла',
    filterPurple: 'Пурпур',
    filterNature: 'Природа',
    filterVintage: 'Вінтаж',
    filterWarm: 'Тепло',
    filterCool: 'Холод',
    filterSunrise: 'Схід сонця',
    filterNoir: 'Нуар',
    
    // Image Editor
    replaceImage: 'Замінити зображення',
    undo: 'Скасувати',
    redo: 'Повторити',
    flipHorizontal: 'Відобразити горизонтально',
    flipVertical: 'Відобразити вертикально',
    deleteImage: 'Видалити',
    confirm: 'Підтвердити',
    moveUp: 'Вгору',
    moveDown: 'Вниз',
    moveLeft: 'Вліво',
    moveRight: 'Вправо',
    scaleDown: 'Зменшити',
    scaleUp: 'Збільшити',
    
    // Text Size
    textSize: 'Розмір тексту',
    textSizeSmall: 'S',
    textSizeMedium: 'M',
    textSizeLarge: 'L',
    
    // Social Share
    shareWithFriends: 'Поділитися з друзями',
    shareOn: 'Поділитися в',
    copyLink: 'Копіювати посилання',
    linkCopied: 'Посилання скопійовано!',
    
    // Size Presets
    sizeSquare: 'Квадрат',
    sizeInstagram: 'Instagram',
    sizeFullHD: 'Full HD',
    sizeStory: 'Сторіс',
    sizeFacebook: 'Facebook',
    sizeTwitter: 'Twitter',
    size2K: '2K',
    
    // Stickers
    stickers: 'Стікери',
    uploadSticker: 'Завантажити стікер',
    noStickersYet: 'Ще немає стікерів. Завантажте зображення як стікери.',
    stickerLabel: 'Стікер',
    stickerScale: 'Розмір',
    stickerRotation: 'Поворот',
    dragStickerOnCanvas: 'Перетягніть стікери на полотно',
  },
  
  ru: {
    // Meta/SEO
    siteTitle: 'CollageEditor — Создавай красивые коллажи онлайн',
    siteDescription: 'Бесплатный онлайн редактор фотоколлажей. Выбирай шаблоны, загружай фото, настраивай стиль и скачивай готовый коллаж.',
    hiddenH1: 'Онлайн редактор фотоколлажей',
    
    // Header
    headerTitle: 'CollageEditor',
    headerSubtitle: 'Создавай красоту',
    
    // Buttons
    reset: 'Сброс',
    download: 'Скачать',
    
    // Tabs
    templates: 'Шаблоны',
    settings: 'Настройки',
    adjustments: 'Коррекция',
    text: 'Текст',
    background: 'Фон',
    filters: 'Фильтры',
    
    // Messages
    templateCreated: 'Шаблон создан',
    gridReady: 'Сетка готова',
    resetDone: 'Сброшено',
    allChangesReverted: 'Все изменения отменены',
    preparing: 'Подготовка...',
    creatingCollage: 'Создаём ваш коллаж',
    done: 'Готово!',
    collageSaved: 'Коллаж сохранён в формате',
    error: 'Ошибка',
    couldNotSave: 'Не удалось сохранить коллаж',
    newText: 'Новый текст',
    clickOrDragHint: 'Нажмите на ячейку или перетащите изображение',
    imageHintDragZoom: 'Перетащите • Прокрутка для масштаба',
    imageHintDragZoomMobile: 'Перетащите • Щипок для масштаба',
    
    // Features section
    featuresTitle: 'Почему CollageEditor?',
    featuresSubtitle: 'Всё необходимое для создания потрясающих фотоколлажей',
    feature1Title: 'Простота использования',
    feature1Description: 'Интуитивный интерфейс перетаскивания. Не нужны навыки дизайна — создавайте профессиональные коллажи за считанные минуты.',
    feature2Title: 'Красивые шаблоны',
    feature2Description: 'Выбирайте из множества готовых шаблонов или создавайте собственные макеты сетки.',
    feature3Title: 'Расширенное редактирование',
    feature3Description: 'Применяйте фильтры, настраивайте цвета, добавляйте текст и совершенствуйте каждую деталь коллажа.',
    feature4Title: 'Высококачественный экспорт',
    feature4Description: 'Скачивайте коллажи в высоком разрешении PNG или JPG, идеально для печати или публикации.',
    
    // Footer
    footerCopyright: '© 2026 CollageEditor. Все права защищены.',
    privacyPolicy: 'Политика конфиденциальности',
    contact: 'Контакты',
    
    // Privacy page
    privacyTitle: 'Политика конфиденциальности',
    privacyContent: `Данная Политика конфиденциальности описывает, как сервис CollageEditor обрабатывает информацию пользователей.

### 1. Какую информацию мы НЕ собираем

Мы не сохраняем, не передаём и не загружаем:

- фотографии и изображения

- личные файлы пользователей

Вся обработка изображений происходит непосредственно в вашем браузере.

### 2. Какую информацию мы можем собирать

Мы можем собирать ограниченную техническую информацию:

- тип браузера

- тип устройства

- операционную систему

- страну пользователя

- анонимную статистику использования

Эти данные используются только для улучшения сервиса.

### 3. Cookies и аналитика

Мы можем использовать cookies и сторонние аналитические сервисы (например, Google Analytics).

Cookies не содержат персональных данных и могут быть отключены в настройках браузера.

### 4. Сторонние сервисы

Мы можем использовать сторонние сервисы для:

- аналитики

- рекламы

- обработки платежей (при наличии)

Такие сервисы имеют собственные политики конфиденциальности.

### 5. Защита данных

Мы принимаем разумные меры для защиты сайта.

Изображения не хранятся на наших серверах.

### 6. Дети

Сервис не предназначен для лиц младше 13 лет.

### 7. Изменения политики

Политика может обновляться. Актуальная версия всегда доступна на этой странице.

### 8. Контакты

По всем вопросам обращайтесь по адресу https://collageeditor.com/ru/contact`,
    
    // Contact page
    contactTitle: 'Свяжитесь с нами',
    contactDescription: 'Есть вопросы или отзывы? Мы будем рады услышать вас!',
    contactName: 'Ваше имя',
    contactEmail: 'Ваш Email',
    contactMessage: 'Сообщение',
    contactSend: 'Отправить',
    contactSuccess: 'Сообщение успешно отправлено!',
    showEmail: 'Показать Email',
    clickToSendEmail: 'Нажмите чтобы отправить письмо',
    noEmailConfigured: 'Email для связи не настроен',
    
    // Admin
    adminTitle: 'Админ панель',
    adminLogin: 'Вход',
    adminPassword: 'Пароль',
    adminEnter: 'Войти',
    adminWrongPassword: 'Неверный пароль',
    adminSaveInfo: 'Изменения показываются для предпросмотра. Чтобы применить их навсегда, отредактируйте файлы переводов в кодовой базе.',
    
    // Collage Editor UI
    upload: 'Загрузить',
    allTemplates: 'Все шаблоны',
    hideTemplates: 'Скрыть шаблоны',
    createTemplate: 'Создать шаблон',
    templateGenerator: 'Генератор шаблонов',
    rows: 'Рядов',
    columns: 'Колонок',
    cellsWillBeCreated: 'будет создано ячеек',
    applyTemplate: 'Применить шаблон',
    canvasSize: 'Размер холста',
    selectSize: 'Выберите размер',
    width: 'Ширина',
    height: 'Высота',
    cellGap: 'Отступы между ячейками',
    cellRounding: 'Скругление ячеек',
    canvasBorder: 'Рамка холста',
    cellBorder: 'Рамка ячеек',
    borderColor: 'Цвет рамки',
    collageBackground: 'Фон коллажа',
    uploadBackground: 'Загрузить свой фон',
    colors: 'Цвета',
    gradients: 'Градиенты',
    captions: 'Надписи',
    add: 'Добавить',
    clickAddToCreateCaption: 'Нажмите "Добавить" чтобы создать надпись',
    enterText: 'Введите текст...',
    font: 'Шрифт',
    size: 'Размер',
    color: 'Цвет',
    shadow: 'Тень',
    stroke: 'Контур',
    strokeColor: 'Цвет контура',
    strokeWidth: 'Толщина контура',
    dragTextOnCanvas: 'Перетащите текст на холсте',
    textAlignment: 'Выравнивание',
    brightness: 'Яркость',
    contrast: 'Контраст',
    saturation: 'Насыщенность',
    faqTitle: 'FAQ',
    
    // Adjustments Panel
    imageEnhancement: 'Улучшение изображения',
    resetAdjustments: 'Сброс',
    adjustmentsApplyToAll: 'Изменения применяются ко всему коллажу',
    
    // Filters Panel
    filtersTitle: 'Фильтры',
    filterOriginal: 'Оригинал',
    filter1977: '1977',
    filterBrannan: 'Брэнан',
    filterGotham: 'Готэм',
    filterHefe: 'Хэфе',
    filterGraymouse: 'Серый мышь',
    filterKelvin: 'Кельвин',
    filterNashville: 'Нэшвилл',
    filterXpro2: 'X-PRO II',
    filterAzure: 'Лазурь',
    filterMist: 'Мгла',
    filterPurple: 'Пурпур',
    filterNature: 'Естество',
    filterVintage: 'Олдскул',
    filterWarm: 'Тепло',
    filterCool: 'Холод',
    filterSunrise: 'Восход',
    filterNoir: 'Нуар',
    
    // Image Editor
    replaceImage: 'Заменить изображение',
    undo: 'Отменить',
    redo: 'Повторить',
    flipHorizontal: 'Отразить по горизонтали',
    flipVertical: 'Отразить по вертикали',
    deleteImage: 'Удалить',
    confirm: 'Подтвердить',
    moveUp: 'Вверх',
    moveDown: 'Вниз',
    moveLeft: 'Влево',
    moveRight: 'Вправо',
    scaleDown: 'Уменьшить',
    scaleUp: 'Увеличить',
    
    // Text Size
    textSize: 'Размер текста',
    textSizeSmall: 'S',
    textSizeMedium: 'M',
    textSizeLarge: 'L',
    
    // Social Share
    shareWithFriends: 'Поделиться с друзьями',
    shareOn: 'Поделиться в',
    copyLink: 'Копировать ссылку',
    linkCopied: 'Ссылка скопирована!',
    
    // Size Presets
    sizeSquare: 'Квадрат',
    sizeInstagram: 'Instagram',
    sizeFullHD: 'Full HD',
    sizeStory: 'Сторис',
    sizeFacebook: 'Facebook',
    sizeTwitter: 'Twitter',
    size2K: '2K',
    
    // Stickers
    stickers: 'Стикеры',
    uploadSticker: 'Загрузить стикер',
    noStickersYet: 'Ещё нет стикеров. Загрузите изображения как стикеры.',
    stickerLabel: 'Стикер',
    stickerScale: 'Размер',
    stickerRotation: 'Поворот',
    dragStickerOnCanvas: 'Перетащите стикеры на холсте',
  },

  es: {
    // Meta/SEO
    siteTitle: 'CollageEditor — Crea hermosos collages en línea',
    siteDescription: 'Editor de collages de fotos gratuito en línea. Elige plantillas, sube fotos, personaliza el estilo y descarga tu collage.',
    hiddenH1: 'Creador de collages de fotos online',
    
    // Header
    headerTitle: 'CollageEditor',
    headerSubtitle: 'Crea belleza',
    
    // Buttons
    reset: 'Restablecer',
    download: 'Descargar',
    
    // Tabs
    templates: 'Plantillas',
    settings: 'Ajustes',
    adjustments: 'Ajustes',
    text: 'Texto',
    background: 'Fondo',
    filters: 'Filtros',
    
    // Messages
    templateCreated: 'Plantilla creada',
    gridReady: 'Cuadrícula lista',
    resetDone: 'Restablecido',
    allChangesReverted: 'Todos los cambios revertidos',
    preparing: 'Preparando...',
    creatingCollage: 'Creando tu collage',
    done: '¡Listo!',
    collageSaved: 'Collage guardado como',
    error: 'Error',
    couldNotSave: 'No se pudo guardar el collage',
    newText: 'Nuevo texto',
    clickOrDragHint: 'Haz clic en una celda o arrastra una imagen',
    imageHintDragZoom: 'Arrastra • Desplaza para zoom',
    imageHintDragZoomMobile: 'Arrastra • Pellizca para zoom',
    
    // Features section
    featuresTitle: '¿Por qué CollageEditor?',
    featuresSubtitle: 'Todo lo que necesitas para crear impresionantes collages de fotos',
    feature1Title: 'Fácil de usar',
    feature1Description: 'Interfaz intuitiva de arrastrar y soltar. Sin necesidad de habilidades de diseño: crea collages profesionales en minutos.',
    feature2Title: 'Plantillas hermosas',
    feature2Description: 'Elige entre una variedad de plantillas prediseñadas o crea tus propios diseños de cuadrícula.',
    feature3Title: 'Edición avanzada',
    feature3Description: 'Aplica filtros, ajusta colores, añade superposiciones de texto y perfecciona cada detalle de tu collage.',
    feature4Title: 'Exportación de alta calidad',
    feature4Description: 'Descarga tus collages en alta resolución PNG o JPG, perfecto para imprimir o compartir.',
    
    // Footer
    footerCopyright: '© 2026 CollageEditor. Todos los derechos reservados.',
    privacyPolicy: 'Política de privacidad',
    contact: 'Contacto',
    
    // Privacy page
    privacyTitle: 'Política de privacidad',
    privacyContent: `Esta Política de Privacidad describe cómo el servicio CollageEditor maneja la información del usuario.

### 1. Qué información NO recopilamos

No almacenamos, transmitimos ni subimos:

- fotos e imágenes

- archivos personales de usuarios

Todo el procesamiento de imágenes se realiza directamente en tu navegador.

### 2. Qué información podemos recopilar

Podemos recopilar información técnica limitada:

- tipo de navegador

- tipo de dispositivo

- sistema operativo

- país del usuario

- estadísticas de uso anónimas

Estos datos se utilizan solo para mejorar el servicio.

### 3. Cookies y análisis

Podemos usar cookies y servicios de análisis de terceros (por ejemplo, Google Analytics).

Las cookies no contienen datos personales y se pueden desactivar en la configuración del navegador.

### 4. Servicios de terceros

Podemos usar servicios de terceros para:

- análisis

- publicidad

- procesamiento de pagos (si corresponde)

Dichos servicios tienen sus propias políticas de privacidad.

### 5. Protección de datos

Tomamos medidas razonables para proteger el sitio web.

Las imágenes no se almacenan en nuestros servidores.

### 6. Niños

El servicio no está destinado a personas menores de 13 años.

### 7. Cambios en la política

La política puede actualizarse. La versión actual siempre está disponible en esta página.

### 8. Contacto

Para todas las consultas, contáctenos en https://collageeditor.com/es/contact`,
    
    // Contact page
    contactTitle: 'Contáctanos',
    contactDescription: '¿Tienes preguntas o comentarios? ¡Nos encantaría saber de ti!',
    contactName: 'Tu nombre',
    contactEmail: 'Tu correo electrónico',
    contactMessage: 'Mensaje',
    contactSend: 'Enviar mensaje',
    contactSuccess: '¡Mensaje enviado con éxito!',
    showEmail: 'Mostrar correo',
    clickToSendEmail: 'Haz clic para enviar un correo',
    noEmailConfigured: 'Correo de contacto no configurado',
    
    // Admin
    adminTitle: 'Panel de administración',
    adminLogin: 'Iniciar sesión',
    adminPassword: 'Contraseña',
    adminEnter: 'Entrar',
    adminWrongPassword: 'Contraseña incorrecta',
    adminSaveInfo: 'Los cambios se muestran para vista previa. Para aplicarlos permanentemente, edita los archivos de traducción.',
    
    // Collage Editor UI
    upload: 'Subir',
    allTemplates: 'Todas las plantillas',
    hideTemplates: 'Ocultar plantillas',
    createTemplate: 'Crear plantilla',
    templateGenerator: 'Generador de plantillas',
    rows: 'Filas',
    columns: 'Columnas',
    cellsWillBeCreated: 'celdas se crearán',
    applyTemplate: 'Aplicar plantilla',
    canvasSize: 'Tamaño del lienzo',
    selectSize: 'Seleccionar tamaño',
    width: 'Ancho',
    height: 'Alto',
    cellGap: 'Espacio entre celdas',
    cellRounding: 'Redondeo de celdas',
    canvasBorder: 'Borde del lienzo',
    cellBorder: 'Borde de celdas',
    borderColor: 'Color del borde',
    collageBackground: 'Fondo del collage',
    uploadBackground: 'Subir fondo',
    colors: 'Colores',
    gradients: 'Degradados',
    captions: 'Leyendas',
    add: 'Añadir',
    clickAddToCreateCaption: 'Haz clic en "Añadir" para crear una leyenda',
    enterText: 'Introduce texto...',
    font: 'Fuente',
    size: 'Tamaño',
    color: 'Color',
    shadow: 'Sombra',
    stroke: 'Contorno',
    strokeColor: 'Color del contorno',
    strokeWidth: 'Grosor del contorno',
    dragTextOnCanvas: 'Arrastra el texto en el lienzo',
    textAlignment: 'Alineación',
    brightness: 'Brillo',
    contrast: 'Contraste',
    saturation: 'Saturación',
    faqTitle: 'Preguntas frecuentes',
    
    // Adjustments Panel
    imageEnhancement: 'Mejora de imagen',
    resetAdjustments: 'Restablecer',
    adjustmentsApplyToAll: 'Los cambios se aplican a todo el collage',
    
    // Filters Panel
    filtersTitle: 'Filtros',
    filterOriginal: 'Original',
    filter1977: '1977',
    filterBrannan: 'Brannan',
    filterGotham: 'Gotham',
    filterHefe: 'Hefe',
    filterGraymouse: 'Ratón gris',
    filterKelvin: 'Kelvin',
    filterNashville: 'Nashville',
    filterXpro2: 'X-PRO II',
    filterAzure: 'Azul',
    filterMist: 'Niebla',
    filterPurple: 'Púrpura',
    filterNature: 'Naturaleza',
    filterVintage: 'Vintage',
    filterWarm: 'Cálido',
    filterCool: 'Frío',
    filterSunrise: 'Amanecer',
    filterNoir: 'Noir',
    
    // Image Editor
    replaceImage: 'Reemplazar imagen',
    undo: 'Deshacer',
    redo: 'Rehacer',
    flipHorizontal: 'Voltear horizontal',
    flipVertical: 'Voltear vertical',
    deleteImage: 'Eliminar',
    confirm: 'Confirmar',
    moveUp: 'Arriba',
    moveDown: 'Abajo',
    moveLeft: 'Izquierda',
    moveRight: 'Derecha',
    scaleDown: 'Reducir',
    scaleUp: 'Ampliar',
    
    // Text Size
    textSize: 'Tamaño del texto',
    textSizeSmall: 'S',
    textSizeMedium: 'M',
    textSizeLarge: 'L',
    
    // Social Share
    shareWithFriends: 'Compartir con amigos',
    shareOn: 'Compartir en',
    copyLink: 'Copiar enlace',
    linkCopied: '¡Enlace copiado!',
    
    // Size Presets
    sizeSquare: 'Cuadrado',
    sizeInstagram: 'Instagram',
    sizeFullHD: 'Full HD',
    sizeStory: 'Historia',
    sizeFacebook: 'Facebook',
    sizeTwitter: 'Twitter',
    size2K: '2K',
    
    // Stickers
    stickers: 'Pegatinas',
    uploadSticker: 'Subir pegatina',
    noStickersYet: 'Aún no hay pegatinas. Sube imágenes para usarlas como pegatinas.',
    stickerLabel: 'Pegatina',
    stickerScale: 'Tamaño',
    stickerRotation: 'Rotación',
    dragStickerOnCanvas: 'Arrastra las pegatinas en el lienzo',
  },

  pt: {
    // Meta/SEO
    siteTitle: 'CollageEditor — Crie lindas colagens online',
    siteDescription: 'Editor de colagens de fotos online gratuito. Escolha modelos, envie fotos, personalize o estilo e baixe sua colagem.',
    hiddenH1: 'Criador de colagens de fotos online',
    
    // Header
    headerTitle: 'CollageEditor',
    headerSubtitle: 'Crie beleza',
    
    // Buttons
    reset: 'Redefinir',
    download: 'Baixar',
    
    // Tabs
    templates: 'Modelos',
    settings: 'Configurações',
    adjustments: 'Ajustes',
    text: 'Texto',
    background: 'Fundo',
    filters: 'Filtros',
    
    // Messages
    templateCreated: 'Modelo criado',
    gridReady: 'Grade pronta',
    resetDone: 'Redefinido',
    allChangesReverted: 'Todas as alterações revertidas',
    preparing: 'Preparando...',
    creatingCollage: 'Criando sua colagem',
    done: 'Pronto!',
    collageSaved: 'Colagem salva como',
    error: 'Erro',
    couldNotSave: 'Não foi possível salvar a colagem',
    newText: 'Novo texto',
    clickOrDragHint: 'Clique em uma célula ou arraste uma imagem',
    imageHintDragZoom: 'Arraste • Role para zoom',
    imageHintDragZoomMobile: 'Arraste • Pinça para zoom',
    
    // Features section
    featuresTitle: 'Por que CollageEditor?',
    featuresSubtitle: 'Tudo o que você precisa para criar colagens de fotos incríveis',
    feature1Title: 'Fácil de usar',
    feature1Description: 'Interface intuitiva de arrastar e soltar. Sem necessidade de habilidades de design — crie colagens profissionais em minutos.',
    feature2Title: 'Modelos bonitos',
    feature2Description: 'Escolha entre uma variedade de modelos pré-projetados ou crie seus próprios layouts de grade.',
    feature3Title: 'Edição avançada',
    feature3Description: 'Aplique filtros, ajuste cores, adicione sobreposições de texto e refine cada detalhe da sua colagem.',
    feature4Title: 'Exportação de alta qualidade',
    feature4Description: 'Baixe suas colagens em alta resolução PNG ou JPG, perfeito para impressão ou compartilhamento.',
    
    // Footer
    footerCopyright: '© 2026 CollageEditor. Todos os direitos reservados.',
    privacyPolicy: 'Política de privacidade',
    contact: 'Contato',
    
    // Privacy page
    privacyTitle: 'Política de privacidade',
    privacyContent: `Esta Política de Privacidade descreve como o serviço CollageEditor lida com as informações do usuário.

### 1. Que informações NÃO coletamos

Não armazenamos, transmitimos ou enviamos:

- fotos e imagens

- arquivos pessoais de usuários

Todo o processamento de imagens ocorre diretamente no seu navegador.

### 2. Que informações podemos coletar

Podemos coletar informações técnicas limitadas:

- tipo de navegador

- tipo de dispositivo

- sistema operacional

- país do usuário

- estatísticas de uso anônimas

Esses dados são usados apenas para melhorar o serviço.

### 3. Cookies e análises

Podemos usar cookies e serviços de análise de terceiros (por exemplo, Google Analytics).

Os cookies não contêm dados pessoais e podem ser desativados nas configurações do navegador.

### 4. Serviços de terceiros

Podemos usar serviços de terceiros para:

- análises

- publicidade

- processamento de pagamentos (se aplicável)

Esses serviços têm suas próprias políticas de privacidade.

### 5. Proteção de dados

Tomamos medidas razoáveis para proteger o site.

As imagens não são armazenadas em nossos servidores.

### 6. Crianças

O serviço não se destina a pessoas menores de 13 anos.

### 7. Alterações na política

A política pode ser atualizada. A versão atual está sempre disponível nesta página.

### 8. Contato

Para todas as perguntas, entre em contato conosco em https://collageeditor.com/pt/contact`,
    
    // Contact page
    contactTitle: 'Entre em contato',
    contactDescription: 'Tem perguntas ou feedback? Adoraríamos ouvir você!',
    contactName: 'Seu nome',
    contactEmail: 'Seu e-mail',
    contactMessage: 'Mensagem',
    contactSend: 'Enviar mensagem',
    contactSuccess: 'Mensagem enviada com sucesso!',
    showEmail: 'Mostrar e-mail',
    clickToSendEmail: 'Clique para enviar um e-mail',
    noEmailConfigured: 'E-mail de contato não configurado',
    
    // Admin
    adminTitle: 'Painel de administração',
    adminLogin: 'Entrar',
    adminPassword: 'Senha',
    adminEnter: 'Entrar',
    adminWrongPassword: 'Senha incorreta',
    adminSaveInfo: 'As alterações são mostradas para visualização. Para aplicá-las permanentemente, edite os arquivos de tradução.',
    
    // Collage Editor UI
    upload: 'Enviar',
    allTemplates: 'Todos os modelos',
    hideTemplates: 'Ocultar modelos',
    createTemplate: 'Criar modelo',
    templateGenerator: 'Gerador de modelos',
    rows: 'Linhas',
    columns: 'Colunas',
    cellsWillBeCreated: 'células serão criadas',
    applyTemplate: 'Aplicar modelo',
    canvasSize: 'Tamanho da tela',
    selectSize: 'Selecionar tamanho',
    width: 'Largura',
    height: 'Altura',
    cellGap: 'Espaço entre células',
    cellRounding: 'Arredondamento de células',
    canvasBorder: 'Borda da tela',
    cellBorder: 'Borda das células',
    borderColor: 'Cor da borda',
    collageBackground: 'Fundo da colagem',
    uploadBackground: 'Enviar fundo',
    colors: 'Cores',
    gradients: 'Gradientes',
    captions: 'Legendas',
    add: 'Adicionar',
    clickAddToCreateCaption: 'Clique em "Adicionar" para criar uma legenda',
    enterText: 'Digite o texto...',
    font: 'Fonte',
    size: 'Tamanho',
    color: 'Cor',
    shadow: 'Sombra',
    stroke: 'Contorno',
    strokeColor: 'Cor do contorno',
    strokeWidth: 'Espessura do contorno',
    dragTextOnCanvas: 'Arraste o texto na tela',
    textAlignment: 'Alinhamento',
    brightness: 'Brilho',
    contrast: 'Contraste',
    saturation: 'Saturação',
    faqTitle: 'Perguntas frequentes',
    
    // Adjustments Panel
    imageEnhancement: 'Melhoria de imagem',
    resetAdjustments: 'Redefinir',
    adjustmentsApplyToAll: 'As alterações se aplicam a toda a colagem',
    
    // Filters Panel
    filtersTitle: 'Filtros',
    filterOriginal: 'Original',
    filter1977: '1977',
    filterBrannan: 'Brannan',
    filterGotham: 'Gotham',
    filterHefe: 'Hefe',
    filterGraymouse: 'Rato cinza',
    filterKelvin: 'Kelvin',
    filterNashville: 'Nashville',
    filterXpro2: 'X-PRO II',
    filterAzure: 'Azul',
    filterMist: 'Névoa',
    filterPurple: 'Roxo',
    filterNature: 'Natureza',
    filterVintage: 'Vintage',
    filterWarm: 'Quente',
    filterCool: 'Frio',
    filterSunrise: 'Amanhecer',
    filterNoir: 'Noir',
    
    // Image Editor
    replaceImage: 'Substituir imagem',
    undo: 'Desfazer',
    redo: 'Refazer',
    flipHorizontal: 'Virar horizontal',
    flipVertical: 'Virar vertical',
    deleteImage: 'Excluir',
    confirm: 'Confirmar',
    moveUp: 'Para cima',
    moveDown: 'Para baixo',
    moveLeft: 'Esquerda',
    moveRight: 'Direita',
    scaleDown: 'Diminuir',
    scaleUp: 'Aumentar',
    
    // Text Size
    textSize: 'Tamanho do texto',
    textSizeSmall: 'S',
    textSizeMedium: 'M',
    textSizeLarge: 'L',
    
    // Social Share
    shareWithFriends: 'Compartilhar com amigos',
    shareOn: 'Compartilhar em',
    copyLink: 'Copiar link',
    linkCopied: 'Link copiado!',
    
    // Size Presets
    sizeSquare: 'Quadrado',
    sizeInstagram: 'Instagram',
    sizeFullHD: 'Full HD',
    sizeStory: 'Story',
    sizeFacebook: 'Facebook',
    sizeTwitter: 'Twitter',
    size2K: '2K',
    
    // Stickers
    stickers: 'Adesivos',
    uploadSticker: 'Enviar adesivo',
    noStickersYet: 'Ainda não há adesivos. Envie imagens para usar como adesivos.',
    stickerLabel: 'Adesivo',
    stickerScale: 'Tamanho',
    stickerRotation: 'Rotação',
    dragStickerOnCanvas: 'Arraste os adesivos na tela',
  },

  fr: {
    // Meta/SEO
    siteTitle: 'CollageEditor — Créez de beaux collages en ligne',
    siteDescription: 'Éditeur de collages photo gratuit en ligne. Choisissez des modèles, téléchargez des photos, personnalisez le style et téléchargez votre collage.',
    hiddenH1: 'Créateur de collages photo en ligne',
    
    // Header
    headerTitle: 'CollageEditor',
    headerSubtitle: 'Créez la beauté',
    
    // Buttons
    reset: 'Réinitialiser',
    download: 'Télécharger',
    
    // Tabs
    templates: 'Modèles',
    settings: 'Paramètres',
    adjustments: 'Ajustements',
    text: 'Texte',
    background: 'Arrière-plan',
    filters: 'Filtres',
    
    // Messages
    templateCreated: 'Modèle créé',
    gridReady: 'Grille prête',
    resetDone: 'Réinitialisé',
    allChangesReverted: 'Toutes les modifications annulées',
    preparing: 'Préparation...',
    creatingCollage: 'Création de votre collage',
    done: 'Terminé !',
    collageSaved: 'Collage enregistré sous',
    error: 'Erreur',
    couldNotSave: 'Impossible d\'enregistrer le collage',
    newText: 'Nouveau texte',
    clickOrDragHint: 'Cliquez sur une cellule ou faites glisser une image',
    imageHintDragZoom: 'Glissez • Défilez pour zoomer',
    imageHintDragZoomMobile: 'Glissez • Pincez pour zoomer',
    
    // Features section
    featuresTitle: 'Pourquoi CollageEditor ?',
    featuresSubtitle: 'Tout ce dont vous avez besoin pour créer de superbes collages photo',
    feature1Title: 'Facile à utiliser',
    feature1Description: 'Interface intuitive de glisser-déposer. Aucune compétence en design requise — créez des collages professionnels en quelques minutes.',
    feature2Title: 'Beaux modèles',
    feature2Description: 'Choisissez parmi une variété de modèles prédéfinis ou créez vos propres dispositions de grille.',
    feature3Title: 'Édition avancée',
    feature3Description: 'Appliquez des filtres, ajustez les couleurs, ajoutez des superpositions de texte et peaufinez chaque détail de votre collage.',
    feature4Title: 'Exportation haute qualité',
    feature4Description: 'Téléchargez vos collages en haute résolution PNG ou JPG, parfait pour l\'impression ou le partage.',
    
    // Footer
    footerCopyright: '© 2026 CollageEditor. Tous droits réservés.',
    privacyPolicy: 'Politique de confidentialité',
    contact: 'Contact',
    
    // Privacy page
    privacyTitle: 'Politique de confidentialité',
    privacyContent: `Cette Politique de Confidentialité décrit comment le service CollageEditor traite les informations des utilisateurs.

### 1. Quelles informations nous NE collectons PAS

Nous ne stockons, ne transmettons ni ne téléchargeons :

- photos et images

- fichiers personnels des utilisateurs

Tout le traitement des images se fait directement dans votre navigateur.

### 2. Quelles informations nous pouvons collecter

Nous pouvons collecter des informations techniques limitées :

- type de navigateur

- type d'appareil

- système d'exploitation

- pays de l'utilisateur

- statistiques d'utilisation anonymes

Ces données sont utilisées uniquement pour améliorer le service.

### 3. Cookies et analyses

Nous pouvons utiliser des cookies et des services d'analyse tiers (par exemple, Google Analytics).

Les cookies ne contiennent pas de données personnelles et peuvent être désactivés dans les paramètres du navigateur.

### 4. Services tiers

Nous pouvons utiliser des services tiers pour :

- l'analyse

- la publicité

- le traitement des paiements (le cas échéant)

Ces services ont leurs propres politiques de confidentialité.

### 5. Protection des données

Nous prenons des mesures raisonnables pour protéger le site.

Les images ne sont pas stockées sur nos serveurs.

### 6. Enfants

Le service n'est pas destiné aux personnes de moins de 13 ans.

### 7. Modifications de la politique

La politique peut être mise à jour. La version actuelle est toujours disponible sur cette page.

### 8. Contact

Pour toute question, contactez-nous à https://collageeditor.com/fr/contact`,
    
    // Contact page
    contactTitle: 'Contactez-nous',
    contactDescription: 'Vous avez des questions ou des commentaires ? Nous serions ravis de vous entendre !',
    contactName: 'Votre nom',
    contactEmail: 'Votre e-mail',
    contactMessage: 'Message',
    contactSend: 'Envoyer le message',
    contactSuccess: 'Message envoyé avec succès !',
    showEmail: 'Afficher l\'e-mail',
    clickToSendEmail: 'Cliquez pour envoyer un e-mail',
    noEmailConfigured: 'E-mail de contact non configuré',
    
    // Admin
    adminTitle: 'Panneau d\'administration',
    adminLogin: 'Connexion',
    adminPassword: 'Mot de passe',
    adminEnter: 'Entrer',
    adminWrongPassword: 'Mot de passe incorrect',
    adminSaveInfo: 'Les modifications sont affichées pour aperçu. Pour les appliquer définitivement, modifiez les fichiers de traduction.',
    
    // Collage Editor UI
    upload: 'Télécharger',
    allTemplates: 'Tous les modèles',
    hideTemplates: 'Masquer les modèles',
    createTemplate: 'Créer un modèle',
    templateGenerator: 'Générateur de modèles',
    rows: 'Lignes',
    columns: 'Colonnes',
    cellsWillBeCreated: 'cellules seront créées',
    applyTemplate: 'Appliquer le modèle',
    canvasSize: 'Taille du canevas',
    selectSize: 'Sélectionner la taille',
    width: 'Largeur',
    height: 'Hauteur',
    cellGap: 'Espacement des cellules',
    cellRounding: 'Arrondi des cellules',
    canvasBorder: 'Bordure du canevas',
    cellBorder: 'Bordure des cellules',
    borderColor: 'Couleur de bordure',
    collageBackground: 'Arrière-plan du collage',
    uploadBackground: 'Télécharger un arrière-plan',
    colors: 'Couleurs',
    gradients: 'Dégradés',
    captions: 'Légendes',
    add: 'Ajouter',
    clickAddToCreateCaption: 'Cliquez sur "Ajouter" pour créer une légende',
    enterText: 'Entrez le texte...',
    font: 'Police',
    size: 'Taille',
    color: 'Couleur',
    shadow: 'Ombre',
    stroke: 'Contour',
    strokeColor: 'Couleur du contour',
    strokeWidth: 'Épaisseur du contour',
    dragTextOnCanvas: 'Faites glisser le texte sur le canevas',
    textAlignment: 'Alignement',
    brightness: 'Luminosité',
    contrast: 'Contraste',
    saturation: 'Saturation',
    faqTitle: 'FAQ',
    
    // Adjustments Panel
    imageEnhancement: 'Amélioration d\'image',
    resetAdjustments: 'Réinitialiser',
    adjustmentsApplyToAll: 'Les modifications s\'appliquent à tout le collage',
    
    // Filters Panel
    filtersTitle: 'Filtres',
    filterOriginal: 'Original',
    filter1977: '1977',
    filterBrannan: 'Brannan',
    filterGotham: 'Gotham',
    filterHefe: 'Hefe',
    filterGraymouse: 'Souris grise',
    filterKelvin: 'Kelvin',
    filterNashville: 'Nashville',
    filterXpro2: 'X-PRO II',
    filterAzure: 'Azur',
    filterMist: 'Brume',
    filterPurple: 'Violet',
    filterNature: 'Nature',
    filterVintage: 'Vintage',
    filterWarm: 'Chaud',
    filterCool: 'Froid',
    filterSunrise: 'Lever de soleil',
    filterNoir: 'Noir',
    
    // Image Editor
    replaceImage: 'Remplacer l\'image',
    undo: 'Annuler',
    redo: 'Refaire',
    flipHorizontal: 'Retourner horizontalement',
    flipVertical: 'Retourner verticalement',
    deleteImage: 'Supprimer',
    confirm: 'Confirmer',
    moveUp: 'Haut',
    moveDown: 'Bas',
    moveLeft: 'Gauche',
    moveRight: 'Droite',
    scaleDown: 'Réduire',
    scaleUp: 'Agrandir',
    
    // Text Size
    textSize: 'Taille du texte',
    textSizeSmall: 'S',
    textSizeMedium: 'M',
    textSizeLarge: 'L',
    
    // Social Share
    shareWithFriends: 'Partager avec des amis',
    shareOn: 'Partager sur',
    copyLink: 'Copier le lien',
    linkCopied: 'Lien copié !',
    
    // Size Presets
    sizeSquare: 'Carré',
    sizeInstagram: 'Instagram',
    sizeFullHD: 'Full HD',
    sizeStory: 'Story',
    sizeFacebook: 'Facebook',
    sizeTwitter: 'Twitter',
    size2K: '2K',
    
    // Stickers
    stickers: 'Autocollants',
    uploadSticker: 'Télécharger un autocollant',
    noStickersYet: 'Pas encore d\'autocollants. Téléchargez des images pour les utiliser.',
    stickerLabel: 'Autocollant',
    stickerScale: 'Taille',
    stickerRotation: 'Rotation',
    dragStickerOnCanvas: 'Faites glisser les autocollants sur le canevas',
  },

  de: {
    // Meta/SEO
    siteTitle: 'CollageEditor — Erstellen Sie schöne Collagen online',
    siteDescription: 'Kostenloser Online-Fotocollage-Editor. Wählen Sie Vorlagen, laden Sie Fotos hoch, passen Sie den Stil an und laden Sie Ihre Collage herunter.',
    hiddenH1: 'Online Foto Collage erstellen',
    
    // Header
    headerTitle: 'CollageEditor',
    headerSubtitle: 'Schönheit erschaffen',
    
    // Buttons
    reset: 'Zurücksetzen',
    download: 'Herunterladen',
    
    // Tabs
    templates: 'Vorlagen',
    settings: 'Einstellungen',
    adjustments: 'Anpassungen',
    text: 'Text',
    background: 'Hintergrund',
    filters: 'Filter',
    
    // Messages
    templateCreated: 'Vorlage erstellt',
    gridReady: 'Raster bereit',
    resetDone: 'Zurückgesetzt',
    allChangesReverted: 'Alle Änderungen rückgängig gemacht',
    preparing: 'Vorbereitung...',
    creatingCollage: 'Erstellen Ihrer Collage',
    done: 'Fertig!',
    collageSaved: 'Collage gespeichert als',
    error: 'Fehler',
    couldNotSave: 'Collage konnte nicht gespeichert werden',
    newText: 'Neuer Text',
    clickOrDragHint: 'Klicken Sie auf eine Zelle oder ziehen Sie ein Bild',
    imageHintDragZoom: 'Ziehen • Scrollen zum Zoomen',
    imageHintDragZoomMobile: 'Ziehen • Kneifen zum Zoomen',
    
    // Features section
    featuresTitle: 'Warum CollageEditor?',
    featuresSubtitle: 'Alles, was Sie brauchen, um atemberaubende Fotocollagen zu erstellen',
    feature1Title: 'Einfach zu bedienen',
    feature1Description: 'Intuitive Drag-and-Drop-Oberfläche. Keine Designkenntnisse erforderlich — erstellen Sie professionelle Collagen in Minuten.',
    feature2Title: 'Schöne Vorlagen',
    feature2Description: 'Wählen Sie aus einer Vielzahl vorgefertigter Vorlagen oder erstellen Sie Ihre eigenen Rasterlayouts.',
    feature3Title: 'Erweiterte Bearbeitung',
    feature3Description: 'Wenden Sie Filter an, passen Sie Farben an, fügen Sie Textüberlagerungen hinzu und verfeinern Sie jedes Detail Ihrer Collage.',
    feature4Title: 'Hochwertige Exportierung',
    feature4Description: 'Laden Sie Ihre Collagen in hoher Auflösung als PNG oder JPG herunter, perfekt zum Drucken oder Teilen.',
    
    // Footer
    footerCopyright: '© 2026 CollageEditor. Alle Rechte vorbehalten.',
    privacyPolicy: 'Datenschutzrichtlinie',
    contact: 'Kontakt',
    
    // Privacy page
    privacyTitle: 'Datenschutzrichtlinie',
    privacyContent: `Diese Datenschutzrichtlinie beschreibt, wie der Dienst CollageEditor Benutzerinformationen verarbeitet.

### 1. Welche Informationen wir NICHT sammeln

Wir speichern, übertragen oder laden nicht hoch:

- Fotos und Bilder

- persönliche Dateien von Benutzern

Die gesamte Bildverarbeitung findet direkt in Ihrem Browser statt.

### 2. Welche Informationen wir möglicherweise sammeln

Wir können begrenzte technische Informationen sammeln:

- Browsertyp

- Gerätetyp

- Betriebssystem

- Land des Benutzers

- anonyme Nutzungsstatistiken

Diese Daten werden nur zur Verbesserung des Dienstes verwendet.

### 3. Cookies und Analysen

Wir können Cookies und Analysedienste von Drittanbietern (z.B. Google Analytics) verwenden.

Cookies enthalten keine personenbezogenen Daten und können in den Browsereinstellungen deaktiviert werden.

### 4. Dienste von Drittanbietern

Wir können Dienste von Drittanbietern nutzen für:

- Analysen

- Werbung

- Zahlungsabwicklung (falls zutreffend)

Solche Dienste haben ihre eigenen Datenschutzrichtlinien.

### 5. Datenschutz

Wir ergreifen angemessene Maßnahmen zum Schutz der Website.

Bilder werden nicht auf unseren Servern gespeichert.

### 6. Kinder

Der Dienst ist nicht für Personen unter 13 Jahren bestimmt.

### 7. Änderungen der Richtlinie

Die Richtlinie kann aktualisiert werden. Die aktuelle Version ist immer auf dieser Seite verfügbar.

### 8. Kontakt

Bei allen Fragen kontaktieren Sie uns unter https://collageeditor.com/de/contact`,
    
    // Contact page
    contactTitle: 'Kontaktieren Sie uns',
    contactDescription: 'Haben Sie Fragen oder Feedback? Wir würden gerne von Ihnen hören!',
    contactName: 'Ihr Name',
    contactEmail: 'Ihre E-Mail',
    contactMessage: 'Nachricht',
    contactSend: 'Nachricht senden',
    contactSuccess: 'Nachricht erfolgreich gesendet!',
    showEmail: 'E-Mail anzeigen',
    clickToSendEmail: 'Klicken, um eine E-Mail zu senden',
    noEmailConfigured: 'Kontakt-E-Mail nicht konfiguriert',
    
    // Admin
    adminTitle: 'Admin-Panel',
    adminLogin: 'Anmelden',
    adminPassword: 'Passwort',
    adminEnter: 'Eintreten',
    adminWrongPassword: 'Falsches Passwort',
    adminSaveInfo: 'Änderungen werden zur Vorschau angezeigt. Um sie dauerhaft anzuwenden, bearbeiten Sie die Übersetzungsdateien.',
    
    // Collage Editor UI
    upload: 'Hochladen',
    allTemplates: 'Alle Vorlagen',
    hideTemplates: 'Vorlagen ausblenden',
    createTemplate: 'Vorlage erstellen',
    templateGenerator: 'Vorlagen-Generator',
    rows: 'Zeilen',
    columns: 'Spalten',
    cellsWillBeCreated: 'Zellen werden erstellt',
    applyTemplate: 'Vorlage anwenden',
    canvasSize: 'Leinwandgröße',
    selectSize: 'Größe auswählen',
    width: 'Breite',
    height: 'Höhe',
    cellGap: 'Zellenabstand',
    cellRounding: 'Zellenabrundung',
    canvasBorder: 'Leinwandrahmen',
    cellBorder: 'Zellenrahmen',
    borderColor: 'Rahmenfarbe',
    collageBackground: 'Collage-Hintergrund',
    uploadBackground: 'Hintergrund hochladen',
    colors: 'Farben',
    gradients: 'Verläufe',
    captions: 'Beschriftungen',
    add: 'Hinzufügen',
    clickAddToCreateCaption: 'Klicken Sie auf "Hinzufügen", um eine Beschriftung zu erstellen',
    enterText: 'Text eingeben...',
    font: 'Schriftart',
    size: 'Größe',
    color: 'Farbe',
    shadow: 'Schatten',
    stroke: 'Kontur',
    strokeColor: 'Konturfarbe',
    strokeWidth: 'Konturbreite',
    dragTextOnCanvas: 'Text auf der Leinwand ziehen',
    textAlignment: 'Ausrichtung',
    brightness: 'Helligkeit',
    contrast: 'Kontrast',
    saturation: 'Sättigung',
    faqTitle: 'FAQ',
    
    // Adjustments Panel
    imageEnhancement: 'Bildverbesserung',
    resetAdjustments: 'Zurücksetzen',
    adjustmentsApplyToAll: 'Änderungen gelten für die gesamte Collage',
    
    // Filters Panel
    filtersTitle: 'Filter',
    filterOriginal: 'Original',
    filter1977: '1977',
    filterBrannan: 'Brannan',
    filterGotham: 'Gotham',
    filterHefe: 'Hefe',
    filterGraymouse: 'Graue Maus',
    filterKelvin: 'Kelvin',
    filterNashville: 'Nashville',
    filterXpro2: 'X-PRO II',
    filterAzure: 'Azur',
    filterMist: 'Nebel',
    filterPurple: 'Lila',
    filterNature: 'Natur',
    filterVintage: 'Vintage',
    filterWarm: 'Warm',
    filterCool: 'Kühl',
    filterSunrise: 'Sonnenaufgang',
    filterNoir: 'Noir',
    
    // Image Editor
    replaceImage: 'Bild ersetzen',
    undo: 'Rückgängig',
    redo: 'Wiederholen',
    flipHorizontal: 'Horizontal spiegeln',
    flipVertical: 'Vertikal spiegeln',
    deleteImage: 'Löschen',
    confirm: 'Bestätigen',
    moveUp: 'Nach oben',
    moveDown: 'Nach unten',
    moveLeft: 'Links',
    moveRight: 'Rechts',
    scaleDown: 'Verkleinern',
    scaleUp: 'Vergrößern',
    
    // Text Size
    textSize: 'Textgröße',
    textSizeSmall: 'S',
    textSizeMedium: 'M',
    textSizeLarge: 'L',
    
    // Social Share
    shareWithFriends: 'Mit Freunden teilen',
    shareOn: 'Teilen auf',
    copyLink: 'Link kopieren',
    linkCopied: 'Link kopiert!',
    
    // Size Presets
    sizeSquare: 'Quadrat',
    sizeInstagram: 'Instagram',
    sizeFullHD: 'Full HD',
    sizeStory: 'Story',
    sizeFacebook: 'Facebook',
    sizeTwitter: 'Twitter',
    size2K: '2K',
    
    // Stickers
    stickers: 'Aufkleber',
    uploadSticker: 'Aufkleber hochladen',
    noStickersYet: 'Noch keine Aufkleber. Laden Sie Bilder hoch, um sie als Aufkleber zu verwenden.',
    stickerLabel: 'Aufkleber',
    stickerScale: 'Größe',
    stickerRotation: 'Rotation',
    dragStickerOnCanvas: 'Ziehen Sie die Aufkleber auf die Leinwand',
  },
};

export const getTranslation = (lang: SupportedLanguage): TranslationStrings => {
  return translations[lang] || translations.en;
};
